declare let grecaptcha: ReCaptchaV2.ReCaptcha
declare let scriptLoader: any
import axios from 'axios'
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'
// @ts-ignore
import QueryParams from '@waze/george/lib/query-params'
import { show, hide } from 'core/ui-utils'
import { scopedTranslate } from 'core/ui'
import Dropdown from '../../country_dropdown'
import { parsePhone } from './parse_phone'
import { RECAPTCHA_SITE_KEY } from './config'
import { report } from 'core/services/google_analytics'

// @ts-ignore
import * as template from './template'

const phoneUtil = PhoneNumberUtil.getInstance()

interface ICountry {
  name: string
  countryCode: string
  callingCode: string
}

interface ICountries {
  countries: ICountry[]
}

export enum FEErrorReasons {
  InvalidPhone = 'fe_invalid_phone', // wrong phone format
  InvalidPhoneAfterRecaptcha = 'fe_invalid_phone_after_recaptcha', // wrong phone format after recaptcha challenge
  FailedToSend = 'fe_failed_to_send', // something wnt wrong, should not happen
  CanNotParseResponse = 'fe_can_not_parse_response',
  UnknownErrorCode = 'fe_unknown_error_code'
}
export enum BEErrorReasons {
  be_invalid_calling_code, // wrong country code
  be_invalid_path, // trying to send bad url
  be_failed_to_send, // error from  bifrost
  be_invalid_phone, // wrong phone format
  be_general_error,
  be_response_error, // Got HTTP error response from bifrost
  be_request_not_sent, // Failed to send request to bifrost
  be_recaptcha_error
}
export class SendToPhone {
  private ui!: {
    form: HTMLElement
    country: HTMLElement
    phone: HTMLInputElement
    send: HTMLElement
    success: HTMLElement
    error: HTMLElement
    recaptcha: HTMLElement
  }

  private countryDropdown?: Dropdown
  private client = axios.create({
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  })

  private captchaId: any

  constructor(public el: Element, public link: string = '') {
    scriptLoader
      .waitFor('recaptcha')
      .then(() => {
        this.render()
      })
      .catch((e: any) => {
        console.error(e)
      })
  }

  private async getCountries() {
    try {
      const { data } = await this.client.get<ICountries>('/api/sms/countries')
      let countries = data.countries.sort((a, b) => a.name.localeCompare(b.name))

      const defaultCountry = countries.filter(c => c.countryCode === 'US').pop()
      if (defaultCountry) {
        // Place it first
        countries = countries.filter(c => c.countryCode !== defaultCountry.countryCode)
        countries.unshift(defaultCountry)
      }
      return countries
    } catch {
      return []
    }
  }

  private async render() {
    const countries = await this.getCountries()
    const selectedCountry = countries[0]
    const placeholder = this.getExampleNumber(selectedCountry.countryCode)

    this.el.innerHTML = template({
      countries,
      placeholder,
      t: scopedTranslate('send_to_phone')
    })

    this.ui = {
      form: this.el.querySelector('.ul-send-to-phone') as HTMLElement,
      country: this.el.querySelector('.js-country') as HTMLElement,
      phone: this.el.querySelector('.js-phone') as HTMLInputElement,
      send: this.el.querySelector('.js-send') as HTMLElement,
      success: this.el.querySelector('.js-success') as HTMLElement,
      error: this.el.querySelector('.js-error') as HTMLElement,
      recaptcha: this.el.querySelector('#recaptcha') as HTMLElement
    }

    this.countryDropdown = new Dropdown(this.ui.country)

    this.captchaId = grecaptcha.render(this.ui.recaptcha, {
      sitekey: RECAPTCHA_SITE_KEY,
      callback: this.send
    })

    this.addEventListeners()
    report('send_to_phone_visible', location.href, document.referrer)
  }

  private addEventListeners(): void {
    this.ui.country.addEventListener('change', (e: Event) => {
      this.ui.phone.setAttribute(
        'placeholder',
        this.getExampleNumber((e as CustomEvent).detail.data.iso)
      )
    })

    this.ui.form.addEventListener('submit', (e: Event) => {
      e.preventDefault()
      this.hideMessages()
      if (!this.getPhoneNumber()) {
        this.showErrorMessage(FEErrorReasons.InvalidPhone)
      } else {
        grecaptcha.execute(this.captchaId)
      }
    })
  }

  private displayBackendError(response: any): void {
    let reason = `be_${response.error}`
    if (!(reason in BEErrorReasons)) {
      reason = FEErrorReasons.UnknownErrorCode
    }
    this.showErrorMessage(reason)
  }

  private send = (): void => {
    const recaptchaResponse = grecaptcha.getResponse(this.captchaId)
    grecaptcha.reset()

    const query = QueryParams.stringify({
      utm_source: 'waze_website',
      utm_medium: 'web-universal_links-waze-send_to_phone',
      ...QueryParams.all()
    })
    const path = this.link || `${window.location.pathname}?${query}`

    const phoneNumber = this.getPhoneNumber()
    if (!phoneNumber) {
      this.showErrorMessage(FEErrorReasons.InvalidPhoneAfterRecaptcha)
      return
    }

    this.client
      .post('/api/sms', {
        phone: phoneNumber,
        path,
        recaptcha_response: recaptchaResponse
      })
      .then(() => {
        this.showSuccessMessage(path)
      })
      .catch(e => {
        if (e.response) {
          this.displayBackendError(e.response.data)
        } else if (e.request) {
          this.displayBackendError(String(BEErrorReasons.be_response_error))
        } else {
          this.displayBackendError(String(BEErrorReasons.be_request_not_sent))
        }
      })
  }

  private getPhoneNumber(): string | null {
    const countryCode = this.ui.country.dataset.iso
    const phoneCode = this.ui.country.dataset.code
    const phoneNumber = this.ui.phone.value
    return parsePhone(countryCode, phoneCode, phoneNumber)
  }

  private hideMessages(): void {
    hide(this.ui.success, this.ui.error)
    this.ui.form.classList.remove('is-error')
  }

  private showSuccessMessage(path: string): void {
    show(this.ui.success)
    report('send_to_phone_confirmation_success', location.href, path)
  }

  private showErrorMessage(reason: string): void {
    show(this.ui.error)
    this.ui.form.classList.add('is-error')
    report(`send_to_phone_confirmation_failure_${reason}`, location.href)
  }

  private getExampleNumber(iso: string): string {
    return phoneUtil.format(
      // Typings for the phoneUtil does not include getExampleNumberForType method
      (phoneUtil as any).getExampleNumberForType(iso, PhoneNumberFormat.INTERNATIONAL),
      PhoneNumberFormat.NATIONAL
    )
  }
}
